import React, { useEffect, useState } from 'react';
import styles from './jiraSettings.module.css';
import { Button, Col, Form, message, Row, Select, Switch } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { get } from '../../utils/fetch';
import { ConnectPrompt } from './ConnectPrompt';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          workspace: OptionType;
          space: OptionType;
          folder: OptionType;
          list: OptionType;
          new_tasks: boolean;
          sync_task_updates: boolean;
          new_comments: boolean;
          sync_clickup_comments: boolean;
          sync_clickup_status: boolean;
          task_status: OptionType;
          status_mapping: Record<string, string>;
        };
      }
    | undefined;
  onDisconnect: () => void;
  container?: HTMLElement;
}

const emptyOption = undefined as OptionType | undefined;

const ClickupSettings: React.FC<Props> = ({
  projectId,
  organizationId,
  integration,
  onDisconnect
}) => {
  const [form] = Form.useForm();
  const [selectedWorkspace, setSelectedWorkspace] = useState<
    OptionType | undefined
  >(integration?.settings?.workspace);
  const [selectedSpace, setSelectedSpace] = useState<OptionType | undefined>(
    integration?.settings?.space
  );
  const [selectedFolder, setSelectedFolder] = useState<OptionType | undefined>(
    integration?.settings?.folder
  );
  const [selectedList, setSelectedList] = useState<OptionType | undefined>(
    integration?.settings?.list
  );
  const [selectedStatus, setSelectedStatus] = useState<OptionType | undefined>(
    integration?.settings?.task_status
  );
  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    integration?.settings?.sync_task_updates ?? true
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [syncClickupComments, setSyncClickupComments] = useState(
    integration?.settings?.sync_clickup_comments ?? false
  );
  const [syncClickupStatus, setSyncClickupStatus] = useState(
    integration?.settings?.sync_clickup_status ?? false
  );
  const [statusMappings, setStatusMappings] = useState<Record<string, string>>(
    {}
  );
  const [workspaceOptions, setWorkspaceOptions] = useState<OptionType[]>([]);
  const [spaceOptions, setSpaceOptions] = useState<OptionType[]>([]);
  const [folderOptions, setFolderOptions] = useState<OptionType[]>([]);
  const [folderlessListOptions, setFolderlessListOptions] = useState<
    OptionType[]
  >([]);
  const [folderListOptions, setFolderListOptions] = useState<OptionType[]>([]);
  const [bugherdStatusOptions, setBugherdStatusOptions] = useState<
    OptionType[]
  >([]);
  const [clickupStatusOptions, setClickupStatusOptions] = useState<
    OptionType[]
  >([]);
  const [isWorkspacesLoading, setIsWorkspacesLoading] = useState(false);
  const [isSpacesLoading, setIsSpacesLoading] = useState(false);
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [isListsLoading, setIsListsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isClickupStatusLoading, setIsClickupStatusLoading] = useState(false);

  const listOptions = selectedFolder?.value
    ? folderListOptions
    : folderlessListOptions;

  const invertStatusMappings = (mapping: Record<string, string> = {}) =>
    Object.entries(mapping).reduce(
      (acc, [clickupStatusId, bugherdColumnId]) => {
        acc[bugherdColumnId] = clickupStatusId;
        return acc;
      },
      {} as Record<string, string>
    );

  useEffect(() => {
    if (integration?.settings?.status_mapping) {
      setStatusMappings(
        invertStatusMappings(integration.settings.status_mapping)
      );
    }
  }, [integration]);

  const {
    projectIntegrationId,
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Clickup',
    onDisconnect,
    onConnectedStateLoaded: results => {
      if (!results.settings) return;
      setSelectedWorkspace(results.settings.workspace);
      setSelectedSpace(results.settings.space);
      setSelectedFolder(results.settings.folder);
      setSelectedList(results.settings.list);
      setSelectedStatus(results.settings.task_status);
      setAutoPost(results.settings.new_tasks ?? false);
      setSyncTaskUpdates(results.settings.sync_task_updates ?? true);
      setNewComments(results.settings.new_comments ?? false);
      setSyncClickupComments(results.settings.sync_clickup_comments ?? false);
      setStatusMappings(invertStatusMappings(results.settings.status_mapping));
      form.setFieldsValue({
        workspace: results.settings.workspace?.value,
        space: results.settings.space?.value,
        folder: results.settings.folder?.value,
        list: results.settings.list?.value,
        statusColumn: results.settings.task_status?.value
      });
    }
  });

  const getProjectData = async () => {
    if (!integration?.enabled) return;
    setIsStatusLoading(true);
    try {
      const results = await get(`/projects/full?id=${projectId}`);
      setBugherdStatusOptions(
        results.columns.map(({ id, name }: { id: string; name: string }) => ({
          value: String(id),
          label: name
        }))
      );
    } catch (error) {
      message.error(
        'Failed to fetch BugHerd project details. Please try again.'
      );
    } finally {
      setIsStatusLoading(false);
    }
  };

  const getWorkspaces = async () => {
    if (!integration?.enabled) return;
    setIsWorkspacesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=workspaces`
      );
      setWorkspaceOptions(results);
    } catch (error) {
      message.error('Failed to fetch ClickUp workspaces. Please try again.');
    } finally {
      setIsWorkspacesLoading(false);
    }
  };

  const getSpaces = async (workspaceId: string) => {
    if (!integration?.enabled || !workspaceId) return;
    setIsSpacesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=spaces:${workspaceId}`
      );
      setSpaceOptions(results);
    } catch (error) {
      message.error('Failed to fetch ClickUp spaces. Please try again.');
    } finally {
      setIsSpacesLoading(false);
    }
  };

  const getSpaceLists = async (spaceId: string) => {
    if (!integration?.enabled || !spaceId) return;
    setIsFoldersLoading(true);
    setIsListsLoading(true);
    try {
      const [folders, folderlessLists] = await Promise.all([
        get(
          `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=folders:${spaceId}`
        ),
        get(
          `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=folderless_lists:${spaceId}`
        )
      ]);
      setFolderOptions(folders);
      setFolderlessListOptions(folderlessLists);
    } catch (error) {
      message.error(
        'Failed to fetch ClickUp folders and lists. Please try again.'
      );
    } finally {
      setIsFoldersLoading(false);
      setIsListsLoading(false);
    }
  };

  const getFolderLists = async (folderId: string) => {
    if (!integration?.enabled || !folderId) return;
    setIsListsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=lists:${folderId}`
      );
      setFolderListOptions(results);
    } catch (error) {
      message.error('Failed to fetch ClickUp lists. Please try again.');
    } finally {
      setIsListsLoading(false);
    }
  };

  const getClickupStatuses = async (listId: string) => {
    if (!integration?.enabled || !listId) return;
    setIsClickupStatusLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/clickup_statuses?id=${integration.id}&list_id=${listId}`
      );
      setClickupStatusOptions(results);
    } catch (error) {
      message.error('Failed to fetch ClickUp statuses. Please try again.');
    } finally {
      setIsClickupStatusLoading(false);
    }
  };

  const asOption = (option: OptionType | OptionType[] | undefined) =>
    Array.isArray(option) ? option[0] : option;

  const handleWorkspaceChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = asOption(option);
    setSelectedWorkspace(next);
    setSelectedSpace(emptyOption);
    setSelectedFolder(emptyOption);
    setSelectedList(emptyOption);
    setStatusMappings({});
    form.setFieldsValue({
      space: undefined,
      folder: undefined,
      list: undefined
    });
  };

  const handleSpaceChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = asOption(option);
    setSelectedSpace(next);
    setSelectedFolder(emptyOption);
    setSelectedList(emptyOption);
    setStatusMappings({});
    form.setFieldsValue({ folder: undefined, list: undefined });
  };

  const handleFolderChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = asOption(option);
    setSelectedFolder(next);
    setSelectedList(emptyOption);
    setStatusMappings({});
    form.setFieldsValue({ list: undefined });
  };

  const handleListChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = asOption(option);
    setSelectedList(next);
    setStatusMappings({});
  };

  const handleStatusChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedStatus(asOption(option));
  };

  const handleStatusMappingChange = (
    bugherdColumnId: string,
    clickupStatusId?: string
  ) => {
    setStatusMappings({
      ...statusMappings,
      [bugherdColumnId]: clickupStatusId || ''
    });
  };

  const handleSubmit = () => {
    const validBugherdColumnIds = bugherdStatusOptions.map(
      option => option.value
    );
    const cleanedStatusMappings = Object.keys(statusMappings).reduce(
      (acc, bugherdColumnId) => {
        const clickupStatusId = statusMappings[bugherdColumnId];
        if (!clickupStatusId) return acc;
        if (!validBugherdColumnIds.includes(bugherdColumnId)) return acc;
        acc[clickupStatusId] = bugherdColumnId;
        return acc;
      },
      {} as Record<string, string>
    );

    return saveSettings(
      {
        workspace: selectedWorkspace,
        space: selectedSpace,
        folder: selectedFolder || {},
        list: selectedList,
        new_tasks: autoPost,
        sync_task_updates: syncTaskUpdates,
        new_comments: newComments,
        sync_clickup_comments: syncClickupComments,
        sync_clickup_status: syncClickupStatus,
        task_status: selectedStatus,
        status_mapping: cleanedStatusMappings
      },
      { form }
    );
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState();
      getProjectData();
      getWorkspaces();
    }
  }, [integration, projectId, organizationId]);

  useEffect(() => {
    if (selectedWorkspace?.value) {
      getSpaces(selectedWorkspace.value);
    }
  }, [selectedWorkspace?.value]);

  useEffect(() => {
    if (selectedSpace?.value) {
      getSpaceLists(selectedSpace.value);
    }
  }, [selectedSpace?.value]);

  useEffect(() => {
    if (selectedFolder?.value) {
      getFolderLists(selectedFolder.value);
    }
  }, [selectedFolder?.value]);

  useEffect(() => {
    if (selectedList?.value) {
      getClickupStatuses(selectedList.value);
    }
  }, [selectedList?.value]);

  const renderStatusMappings = () => (
    <div className={styles.statusMappings}>
      <Row className={styles.statusMappingRow} gutter={16}>
        <Col span={11}>
          <strong>BugHerd Status</strong>
        </Col>
        <Col span={2}></Col>
        <Col span={11}>
          <strong>ClickUp Status</strong>
        </Col>
      </Row>
      {bugherdStatusOptions.map(bugherdStatus => (
        <Row
          key={bugherdStatus.value}
          className={styles.statusMappingRow}
          gutter={16}
          align="middle"
        >
          <Col span={11}>
            <span className={styles.jiraStatusLabel}>
              {bugherdStatus.label}
            </span>
          </Col>
          <Col span={2} style={{ textAlign: 'center' }}>
            <span>↔</span>
          </Col>
          <Col span={11}>
            <Select
              className={styles.bugherdColumnSelect}
              placeholder="Select ClickUp status"
              value={statusMappings[bugherdStatus.value] || undefined}
              onChange={value =>
                handleStatusMappingChange(bugherdStatus.value, value)
              }
              options={clickupStatusOptions}
              loading={isClickupStatusLoading}
              allowClear
            />
          </Col>
        </Row>
      ))}
    </div>
  );

  const renderIntegrationSettings = () => (
    <div>
      <Form
        form={form}
        layout="horizontal"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
      >
        <div className={styles.jiraRequired}>
          <div className={styles.requiredBubble}>REQUIRED</div>
          <h3>Connect your ClickUp list</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="workspace"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>ClickUp Workspace
                  </span>
                }
                className={styles.jiraProject}
                rules={[
                  {
                    required: true,
                    message: 'Please select a ClickUp workspace.'
                  }
                ]}
                initialValue={selectedWorkspace?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a workspace"
                  optionFilterProp="label"
                  options={workspaceOptions}
                  onChange={handleWorkspaceChange}
                  loading={isWorkspacesLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          {selectedWorkspace?.value && (
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="space"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>ClickUp Space
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    {
                      required: true,
                      message: 'Please select a ClickUp space.'
                    }
                  ]}
                  initialValue={selectedSpace?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a space"
                    optionFilterProp="label"
                    options={spaceOptions}
                    onChange={handleSpaceChange}
                    loading={isSpacesLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {selectedSpace?.value && (
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="folder"
                  label="ClickUp Folder"
                  className={styles.jiraProject}
                  initialValue={selectedFolder?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a folder if your list is inside one"
                    optionFilterProp="label"
                    options={folderOptions}
                    onChange={handleFolderChange}
                    loading={isFoldersLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {selectedSpace?.value && (
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="list"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>ClickUp List
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    { required: true, message: 'Please select a ClickUp list.' }
                  ]}
                  initialValue={selectedList?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder={
                      selectedFolder?.value
                        ? 'Select a list'
                        : 'Select a folderless list or choose a folder above'
                    }
                    optionFilterProp="label"
                    options={listOptions}
                    onChange={handleListChange}
                    loading={isListsLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>

        <div className={styles.jiraWorkflows}>
          <h3>Choose your desired workflow</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                label="Automatically post new tasks to ClickUp"
                className={styles.autoPost}
              >
                <Switch
                  checked={autoPost}
                  onChange={checked => setAutoPost(checked)}
                />
              </Form.Item>
            </Col>
            {autoPost && (
              <Col span={24}>
                <Form.Item
                  name="statusColumn"
                  label="Create task when status is equal to"
                  className={styles.selectStatus}
                  initialValue={selectedStatus?.value}
                  tooltip="This is an optional field. Select a status to trigger task creation in ClickUp."
                >
                  <Select
                    placeholder="Select a status"
                    options={bugherdStatusOptions}
                    onChange={handleStatusChange}
                    loading={isStatusLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item label="Automatically sync task detail updates to ClickUp">
                <Switch
                  checked={syncTaskUpdates}
                  onChange={setSyncTaskUpdates}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to ClickUp"
              >
                <Switch
                  checked={newComments}
                  onChange={checked => setNewComments(checked)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncComments}
                label="Sync comments from ClickUp to BugHerd"
              >
                <Switch
                  checked={syncClickupComments}
                  onChange={checked => setSyncClickupComments(checked)}
                  disabled={!selectedList?.value}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncStatus}
                label="Sync status updates from ClickUp to BugHerd"
              >
                <Switch
                  checked={syncClickupStatus}
                  onChange={checked => setSyncClickupStatus(checked)}
                  disabled={!selectedList?.value}
                />
              </Form.Item>
            </Col>
          </Row>
          {syncClickupStatus && selectedList?.value && (
            <div className={styles.jiraEditRow}>{renderStatusMappings()}</div>
          )}
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncUsers}
                label="Map assigned users between ClickUp and BugHerd"
              >
                <a
                  href={
                    projectIntegrationId &&
                    selectedWorkspace?.value &&
                    selectedList?.value
                      ? `/settings/organizations/${organizationId}/team?manage_clickup_users=true&integration_id=${integration?.id}&project_integration_id=${projectIntegrationId}&workspace_id=${selectedWorkspace.value}&list_id=${selectedList.value}`
                      : '#'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.manageLink}
                  onClick={event => {
                    if (
                      !projectIntegrationId ||
                      !selectedWorkspace?.value ||
                      !selectedList?.value
                    ) {
                      event.preventDefault();
                      message.warning(
                        'Please save a ClickUp list before managing assigned user mapping.'
                      );
                    }
                  }}
                >
                  Manage <ExportOutlined style={{ marginLeft: '5px' }} />
                </a>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Row
          className={styles.jiraEditRow}
          justify="space-between"
          align="middle"
        >
          <Col>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                className={styles.save}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
          <Col>
            <Button
              type="text"
              shape="round"
              onClick={() => disconnect({ deleteFallback: true })}
              className={styles.disconnectButton}
              danger
            >
              Disconnect
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );

  return (
    <div className={styles.container}>
      {isEnabled ? (
        renderIntegrationSettings()
      ) : (
        <ConnectPrompt
          integrationName="clickup"
          projectId={projectId}
          organizationId={organizationId}
          type="Clickup"
          display="ClickUp"
          provider="clickup"
        />
      )}
    </div>
  );
};

export default ClickupSettings;
